import React, { useCallback, useState } from "react";
import { useFormikContext } from "formik";
import { useApi } from "../../../api/useApi";
import {
  updateUserWorkGroupRole,
  addUser,
} from "../../../api/workgroupMutations";
import { users } from "../../../api/userQueries";
import PagedTable from "../../Table/PagedTable";
import { StyledSelect, FormControl } from "../../Form/FormControls";
import { useSearch } from "../../../Hooks/useSearch";
import SearchInput from "../../../components/Forms/Search/SearchInput";
import ErrorMessages from "../../../components/Notifications/ErrorMessages";

const roles = [
  { label: "INACTIVE", value: "INACTIVE" },
  { label: "ACTIVE", value: "ACTIVE" },
  { label: "OWNER", value: "OWNER" },
];

const SelectUsers = React.memo(
  ({ workGroupId, updateWorkGroup, selectedOrg }) => {
    const {
      values: { users: selectedUsers },
    } = useFormikContext();

    const [{ errors, loading, data }, doFetch] = useApi();
    const [pageSize, setPageSize] = useState(50);

    const userData = data?.users?.edges ?? [];
    const totalCount = data?.users?.totalCount;
    const pageInfo = data?.users?.pageInfo;

    //Fetch for Table Paged

    const fetchData = useCallback(
      ({ pageSize: incomingPageSize, cursor }) => {
        const finalPageSize = incomingPageSize || pageSize;
        doFetch({
          query: users,
          variables: {
            first: finalPageSize,
            after: cursor,
            order: { displayName: "ASC" },
          },
        });
      },
      [doFetch, pageSize]
    );

    const updateSelection = (e, original) => {
      // IF EDITING OWNERS
      const existingIndex = selectedUsers.findIndex(
        (item) => item.member.id === original?.node?.id
      );

      if (existingIndex === -1) {
        const variables = {
          workGroupId: workGroupId,
          newUserId: original?.node?.id,
          role: e.value,
        };
        updateWorkGroup({ query: addUser, variables: variables });
      } else {
        const variables = {
          workGroupId: workGroupId,
          userToUpdate: original?.node?.id,
          newRole: e.value,
        };
        updateWorkGroup({
          query: updateUserWorkGroupRole,
          variables: variables,
        });
      }
    };

    const columnsData = [
      {
        Header: "Name",
        id: "name",
        accessor: (d) => d?.node?.displayName,
      },
      {
        Header: "Email",
        id: "email",
        accessor: (d) => d?.node?.emailAddress,
      },
      {
        Header: () => (
          <div
            style={{
              minWidth: "165px",
              maxWidth: "165px",
            }}
          >
            Role
          </div>
        ),
        id: "role",
        width: 200,
        maxWidth: 200,
        Cell: ({ row: { original } }) => {
          const selected = selectedUsers.find(
            (e) => e.member.id === original?.node?.id
          );
          return (
            <StyledSelect
              className={`react-select-container`}
              classNamePrefix={`react-select`}
              menuPortalTarget={document.body}
              name={`role`}
              id={`role`}
              inputId={`role-input`}
              instanceId={`role-instance`}
              options={roles}
              onChange={(e) => updateSelection(e, original)}
              placeholder={"Select Role"}
              value={selected ? selected.role : null}
            />
          );
        },
      },
    ];

    const onSearch = useCallback(
      (text) => {
        const filterByOrgID =
          selectedOrg?.value === "Entire Platform" ? false : selectedOrg?.value;

        const where = {};
        if (text) {
          where.or = [{ displayName: { contains: text } }];
        }
        if (filterByOrgID) {
          where.organizationId = { eq: filterByOrgID };
        }

        const variables = {
          order: [{ displayName: "ASC" }],
          ...(Object.keys(where).length > 0 && { where }),
          first: pageSize, // Always use current page size
        };

        doFetch({
          query: users,
          variables,
        });
      },
      [doFetch, selectedOrg, pageSize]
    );

    const { inputText, handleInputChange, tooShortWarning } = useSearch({
      onSearch,
    });

    return (
      <>
        <FormControl style={{ maxWidth: "300px" }}>
          <SearchInput
            label="Search User"
            placeholder="Search User"
            inputText={inputText}
            handleChange={handleInputChange}
            warning={tooShortWarning}
          />

          {errors ? <ErrorMessages errors={errors} /> : null}
        </FormControl>

        <PagedTable
          fetchData={fetchData}
          loading={loading}
          pageInfo={pageInfo}
          totalCount={totalCount}
          data={userData}
          columns={columnsData}
          defaultPageSize={50}
          onPageSizeChange={(newSize) => setPageSize(newSize)}
        />
      </>
    );
  }
);

export default SelectUsers;
